import { Injectable, inject } from '@angular/core';
import {DatasetDetail, DatasetType} from './api/fit-api/models/datasets/dataset-type';
import {DatasetService} from './api/fit-api/dataset.service';
import {
	combineLatest,
	forkJoin,
	map,
	Observable,
	of,
	ReplaySubject,
	Subject,
	switchMap,
	tap
} from 'rxjs';
import {SnapshotWithDetail} from './api/fit-api/models/datasets/snapshot';
import {LiveWithDetail} from './api/fit-api/models/datasets/live';
import {BasicAccount, DollarsCategory} from '../models/dollars-category';
import {FinancialSummaryService} from './financial-summary.service';
import {sortByProperty} from './functions/sort-by-property';
import {FilingStatusService} from './filing-status.service';
import {prepareRankings} from './functions/prepare-rankings';
import {FinancialService} from './api/fit-api/financial.service';
import {FinancialSummarySectionId} from './api/fit-api/models/financial-summary';
import {Palette} from '../models/palette';
import {FsSectionFilerCount} from './api/fit-api/models/fs-section-filer-count';
import {FinancialSummarySection} from './api/fit-api/models/snapshots/financial-summary-section';
import {AccountDescriptor} from './api/fit-api/models/snapshots/account-descriptor';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {lowerCaseFirst} from './functions/lower-case-first';
import {Ranking} from '../models/ranking';
import {FitApiService} from './api/fit-api/fit-api.service';
import {TrendAmount} from '../models/trend-amount';
import {ALL_GOVT_TYPES, GovernmentType} from './api/fit-api/models/government-type';
import {LocalGovernment} from './api/fit-api/models/local-government';
import {County} from './api/fit-api/models/snapshots/county';
import {yearsToRangeText} from './functions/years-to-range-text';
import {getPaletteValue} from './functions/get-palette-value';
import {GovernmentSpecificity} from './api/fit-api/models/government-specificity';
import {SchoolsWithDetail} from './api/fit-api/models/datasets/schools';
import {AggregationByGovType} from './api/fit-api/models/aggregation-by-gov-type';
import {Dataset} from './api/fit-api/models/datasets/dataset';
import {AggregationByGovt} from './api/fit-api/models/aggregation-by-govt';
import {PivotTable} from '../models/pivot-table/pivot-table';
import Excel from 'exceljs';
import {DownloadService} from './download.service';
import {Tab} from '../components/tabs/tab';
import {FundField, SelectedFundNode} from '../components/funds-selector/fund-node-model';
import {ALL_COUNTIES, SelectedCounties} from '../components/counties-selector/counties-model';
import {GovTypeCode} from './api/fit-api/models/gov-type-code';
import {GovernmentId} from './api/fit-api/models/government-id';
import {DollarsProfileOptionalRouteParams} from '../models/route-params/dollars-profile-optional-route-params';
import {DataCellAnnotation} from '../models/pivot-table/data-cell-annotation';
import {missingFilingReasonLookup} from './api/fit-api/models/missing-filing-reason';
import {PivotAnnotationType} from '../models/pivot-table/pivot-annotation';
import {FilterService} from './filter.service';
import {SortedBy} from '../models/sorted-by';
import {DataGridService} from '../components/data-grid/data-grid.service';
import {ALL_ACCOUNTING_BASES, SelectedAccountingBases} from "./api/fit-api/models/accounting-basis";

@Injectable({
	providedIn: 'any'
})
export class DollarsCategoryService {
	private datasetService = inject(DatasetService);
	private financialSummaryService = inject(FinancialSummaryService);
	private financialService = inject(FinancialService);
	private filingStatusService = inject(FilingStatusService);
	private route = inject(ActivatedRoute);
	private fitApiService = inject(FitApiService);
	private downloadService = inject(DownloadService);
	private filterService = inject(FilterService);
	private router = inject(Router);

	// Change the current page number; everything goes through the URL, so the original contextual page has to subscribe to these changes
	userRequestsChangeToPageNumber = new Subject<number | null>();
	userRequestsChangeToPageSize = new Subject<number | null>();
	userRequestsChangeToSortedBy = new Subject<string>();
	navigateToCorrectPageForScrollToMcag = new Subject<{ pageToChangeTo: number, scrollToMcag: string }>();
	defaultSortedBy?: SortedBy;

	public static supportedDatasetTypes = [
		DatasetType.Snapshot, // Default dataset type should be first in the array
	] as const;

	public static excludedFSSectionIds: Array<number> = [
		80, // Balance Sheet
	];

	// ordered BARS hierarchy list used for schedule 1 aggregations
	public static defaultBARSHierarchy: Array<string> = [
		'fsSectionId',
		'basicAccountId',
		'subAccountId',
		'elementId',
		'subElementId'
	];

	// data for by dollars profile (parent and child)
	color?: Palette;
	colorValue?: string; // For chart rendering

	logicalAccount = this.route.data.pipe(
		map(routeData => routeData['logicalAccount'] as FinancialSummarySection | AccountDescriptor)
	);

	dollarsFieldName = this.logicalAccount.pipe(map(logicalAccount => {
		if (this.isAccountDescriptor(logicalAccount)) {
			// BARS Account
			return lowerCaseFirst(logicalAccount.acctSgmt + 'Id');
		} else {
			return 'fsSectionId';
		}
	}));

	dollarsId = this.logicalAccount.pipe(map(logicalAccount => logicalAccount.id));

	/**
	 * DatasetsDetail for this route.  This profile page will use a merged multi-dataset approach similar to the
	 * Financial Health profile page.
	 */
	datasetsDetail = this.route.data.pipe(
		map(data => data['datasets'] as Array<Dataset>),
		// create an observable call for each dataset and recombine with forkJoin
		switchMap(datasets =>
			forkJoin(
				datasets.map(dataset => this.datasetService.getDatasetDetail(dataset.datasetType, dataset.id))
			)
		)
	);

	/**
	 * Fiscal display year for this route.
	 */
	fiscalYear = new ReplaySubject<number>(1);

	/**
	 * Fiscal display year for this route.
	 */
	defaultFiscalYear: Observable<number> = this.datasetService.getDefaultYear(this.datasetsDetail);

	// Selected tab (overview, by government, by government type)
	selectedTab?: Tab;

	categoryTitle?: string;
	categoryTitle$ = this.route.title.subscribe(title => this.categoryTitle = title );

	yearRangeForTrendLine = this.fiscalYear.pipe(map(year =>
		yearsToRangeText([year, year ? year - DatasetService.DEFAULT_TREND_YEARS + 1 : null])
	));

	/**
	 * Totals by year for field, no subcategory or gov detail
	 * Use case - trendline graph
	 */
	categoryTotalsForYearRange = combineLatest([this.datasetsDetail, this.fiscalYear, this.dollarsFieldName, this.dollarsId])
		.pipe(switchMap(([datasetsDetail, displayYear, dollarsFieldName, dollarsId]) => {
			if (displayYear) {
				return this.getTotalsByField(datasetsDetail, displayYear, displayYear - DatasetService.DEFAULT_TREND_YEARS + 1, dollarsFieldName, dollarsId);
			} else {
				throw Error('Display year never emitted');
			}
		}));

	/**
	 * Totals for display year for field
	 * Use case - info header total (singular total)
	 */
	categoryTotal = combineLatest([this.categoryTotalsForYearRange, this.fiscalYear])
		.pipe(map(([categoryTotals, fiscalYear]) => {
			// return singular value from schedule 1 aggregations array (value that matches display year)
			// if there isn't a total amount, display zero
			return categoryTotals.find(categoryTotal => categoryTotal.year === fiscalYear)?.totalAmountExclIntlSrvc ?? 0;
		}))

	/**
	 * Data loading dialog for the By Governments & By Government Types pages.
	 */
	isDataLoading = false;

	/**
	 * Process all optional route parameters
	 * @param params
	 * @param fiscalYear
	 * @private
	 */
	processOptionalRouteParams(params: ParamMap, fiscalYear: number) {
		// Start year; calculate default using default # of years for trends
		const appliedStartYear = this.filterService.processStartYearRouteParam(params.get('startYear'), params.get('endYear'), fiscalYear, this.optionalRouteParams);

		// End year
		const appliedEndYear = this.filterService.processEndYearRouteParam(params.get('startYear'), params.get('endYear'), fiscalYear, this.optionalRouteParams);

		// throw error if applied start year is greater than applied end year for any reason
		if (appliedStartYear > appliedEndYear) {
			throw new Error('Error: Applied start year is greater than applied end year');
		}

		// Display unit - does not require data fetch or rebuilding of the data cube/pivot table
		this.filterService.processDisplayUnitRouteParam(params.get('displayUnit'), this.optionalRouteParams);

		// Exclude Internal Service Funds
		this.filterService.processExcludeInternalServiceFundsRouteParam(params.get('excludeInternalServiceFunds'), this.optionalRouteParams);

		// FundNode
		this.filterService.processFundNodeRouteParam(params.get('fundNode'), this.optionalRouteParams);

		// Counties
		this.filterService.processCountiesRouteParam(params.get('counties'), true, this.optionalRouteParams);

		// Accounting Basis
		this.filterService.processAccountingBasesRouteParam(params.get('accountingBases'), true, this.optionalRouteParams);

		// Government Type filter
		this.filterService.processGovTypeCodeRouteParam(params.get('govTypeCode'), this.selectedTab?.id === 'governments', this.optionalRouteParams);

		// Sorted By filter
		// note: not a badge count filter but shared between By Dollars / Gov and By Dollars / Gov type that's why it lives in this function
		this.defaultSortedBy = new SortedBy(fiscalYear, 'descending');
		const appliedSortedBy = DataGridService.processSortedBy(params, this.optionalRouteParams, this.defaultSortedBy);
		this.filterService.sortedBy.next(appliedSortedBy);

		// Recalculate badge count for non-default options
		this.recalculateBadgeCount();
	}

	private recalculateBadgeCount() {
		this.nonDefaultBadgeCount = Object.keys(this.optionalRouteParams).length;

		// If both non-default start and end years are provided, only count as one
		if (this.optionalRouteParams.startYear && this.optionalRouteParams.endYear) {
			this.nonDefaultBadgeCount = this.nonDefaultBadgeCount - 1;
		}

		// Don't count paging/sorted by parameters in badge count
		if (this.optionalRouteParams.page) {
			this.nonDefaultBadgeCount = this.nonDefaultBadgeCount - 1;
		}
		if (this.optionalRouteParams.pageSize) {
			this.nonDefaultBadgeCount = this.nonDefaultBadgeCount - 1;
		}
		if (this.optionalRouteParams.sortedBy) {
			this.nonDefaultBadgeCount = this.nonDefaultBadgeCount - 1;
		}

		// Set to null if 0 to hide the badge
		if (this.nonDefaultBadgeCount === 0) {
			this.nonDefaultBadgeCount = null;
		}
	}

	optionalRouteParams: DollarsProfileOptionalRouteParams = {};

	//region Options Dialog
	nonDefaultBadgeCount: number | null = null;

	appliedDisplayUnit = this.filterService.defaultDisplayUnit;
	displayUnit$ = this.filterService.distinctDisplayUnit
		// If display unit changes (can change without any other routing parameters changing), we need to process just the display unit update
		.subscribe(displayUnit => {
			this.appliedDisplayUnit = displayUnit;
			return this.pivotTable?.updateDisplayUnit(displayUnit)
		});

	// Annotation for by government and by government type pages
	baseAnnotation = new ReplaySubject<string>(1);
	annotation: Observable<string> = combineLatest([
		this.baseAnnotation,
		this.defaultFiscalYear,
		this.filterService.distinctStartYear,
		this.filterService.distinctEndYear,
		this.filterService.distinctExcludeInternalServiceFunds,
		this.filterService.distinctFundNode,
		this.filterService.distinctCounties,
		this.filterService.distinctAccountingBases,
		this.filterService.distinctGovTypeCode,
		this.fitApiService.getFundCategories(),
		this.fitApiService.getFundTypes(),
		this.fitApiService.countiesForList,
		this.fitApiService.accountingBases,
		this.fitApiService.governmentTypesForList
	]).pipe(map(([
		base,
		defaultFiscalYear,
		startYear,
		endYear,
		excludeInternalServiceFunds,
		fundNode,
		counties,
		accountingBases,
		govTypeCode,
		fundCategories,
		fundTypes,
		countyNames,
		accountBasesNames,
		govTypes
	]) => {
		let value = base;

		// Year range
		value += ` (${yearsToRangeText([startYear,endYear])})`;

		if (govTypeCode) {
			const govType = govTypes.find(gt => gt.code === govTypeCode);
			if (govType) {
				value += `; ${govType.descriptionPlural}`;
			}
		}

		if (fundNode?.field !== FundField.allFunds) {
			let fundDesc: string | undefined;

			if (fundNode.field === FundField.fundCategoryId) {
				fundDesc = fundCategories.find(c => c.id === fundNode.value)?.name;
			} else if (fundNode.field === FundField.fundTypeId) {
				fundDesc = fundTypes.find(c => c.id === fundNode.value)?.name;
			}

			if (fundDesc) {
				value += `; ${fundDesc} Funds`;
			}
		}

		value += this.filterService.generateCountiesAnnotation(counties, countyNames, value);
		value += this.filterService.generateAccountingBasesAnnotation(accountingBases, accountBasesNames, value);
		value += this.filterService.generateInternalServiceFundsAnnotation(excludeInternalServiceFunds, value);

		if (startYear && startYear < 2020) {
			value += `; no data for schools before 2020`;
		}

		if (endYear && defaultFiscalYear && endYear > defaultFiscalYear) {
			value += `; data for ${yearsToRangeText([defaultFiscalYear+1,endYear])} is only available for schools currently`;
		}

		return value;
	}));

	/**
	 * Totals for field for display year or year range, no subcategory detail, mcag detail, govType detail, and county detail
	 * Use case - by government lists (pre view model)
	 */
	categoryTotalsByGovtForFiscalYear =
		this.categoryTotalsByGovt(this.fiscalYear, this.fiscalYear);
	categoryTotalsByGovtForYearRange =
		this.categoryTotalsByGovt(
			this.filterService.distinctEndYear.pipe(tap(() => this.isDataLoading = true)),
			this.filterService.distinctStartYear.pipe(tap(() => this.isDataLoading = true)),
			this.filterService.distinctFundNode.pipe(tap(() => this.isDataLoading = true)),
			this.filterService.distinctCounties.pipe(tap(() => this.isDataLoading = true)),
			this.filterService.distinctAccountingBases.pipe(tap(() => this.isDataLoading = true)),
			this.filterService.distinctGovTypeCode.pipe(tap(() => this.isDataLoading = true)),
		);
	categoryTotalsByGovt(
		endYear: Observable<number>,
		startYear: Observable<number>,
		fundNode?: Observable<SelectedFundNode>,
		counties?: Observable<SelectedCounties | undefined>,
		accountingBases?: Observable<SelectedAccountingBases | undefined>,
		govTypeCode?: Observable< ALL_GOVT_TYPES | GovTypeCode | undefined>,
	): Observable<Array<AggregationByGovt>> {
		return combineLatest([
			this.datasetsDetail,
			endYear,
			startYear,
			this.dollarsFieldName,
			this.dollarsId,
			fundNode ?? of(undefined),
			counties ?? of(undefined),
			accountingBases ?? of(undefined),
			govTypeCode ?? of(undefined)
		]).pipe(switchMap(([datasetsDetail, endYear, startYear, dollarsFieldName, dollarsId, fundNode, counties, accountingBases, govTypeCode]) => {
				const queryOverrides =
					this.generateQueryOverridesForLogicalAccount(dollarsFieldName, dollarsId, false, fundNode, counties, accountingBases, govTypeCode);
				return forkJoin(datasetsDetail.map(dataset => {
					if ((dataset instanceof SnapshotWithDetail || dataset instanceof LiveWithDetail)) {
						return this.financialSummaryService.getSchedule1AggregationsByGovt(dataset, endYear, startYear, queryOverrides);
					} else if (dataset instanceof SchoolsWithDetail) {
						return this.financialSummaryService.getOSPIFinancialReportAggregationsByGovt(dataset, endYear, startYear, queryOverrides)
					}
					return of([])
				})).pipe(map(x => x.flat().filter(agg => agg.totalAmountExclIntlSrvc !== 0)));
			}));
	}

	/**
	 * Totals for field for display year or year range for govType detail
	 * Use case - by government type list (pre view model)
	 */
	categoryTotalsByGovTypeForFiscalYear = this.categoryTotalsByGovType(this.fiscalYear, this.fiscalYear);
	categoryTotalsByGovTypeForYearRange =
		this.categoryTotalsByGovType(
			this.filterService.distinctEndYear.pipe(tap(() => this.isDataLoading = true)),
			this.filterService.distinctStartYear.pipe(tap(() => this.isDataLoading = true)),
			this.filterService.distinctFundNode.pipe(tap(() => this.isDataLoading = true)),
			this.filterService.distinctCounties.pipe(tap(() => this.isDataLoading = true)),
			this.filterService.distinctAccountingBases.pipe(tap(() => this.isDataLoading = true))
		);
	categoryTotalsByGovType(
		endYear: Observable<number>,
		startYear: Observable<number>,
		fundNode?: Observable<SelectedFundNode>,
		counties?: Observable<SelectedCounties | undefined>,
		accountingBases?: Observable<SelectedAccountingBases | undefined>
	): Observable<Array<ListRowForDollarsProfile>> {
		return this.categoryTotalsByGovt(endYear, startYear, fundNode, counties, accountingBases)
			.pipe(map(categoryTotals => {
				// a) group governments by government type
				return categoryTotals.reduce((acc: ListRowForDollarsProfile[], curr: AggregationByGovt) => {
					const filer = acc.find((x: ListRowForDollarsProfile) =>
						x.year === curr.year && x.govTypeCode === curr.govTypeCode
					);
					if (filer) {
						filer.totalAmountExclIntlSrvc += curr.totalAmountExclIntlSrvc;
						filer.numberOfGovs ? filer.numberOfGovs += 1 : 0;
					} else {
						// create gov type schedule 1 model out gov model
						// set mcag and county code to zero and start count of govs
						curr.mcag = null;
						curr.countyCode = null;
						acc.push({
							// pass schedule1AggregationByGovt with extended field
							...curr,
							numberOfGovs: 1,
						} as ListRowForDollarsProfile);
					}
					return acc;
				}, []);
			}));
	}

	/**
	 * No data annotations observable for the By Governments page - fills empty data cells with missing filing reason
	 *
	 */
	noDataAnnotations =
		combineLatest([
			this.datasetsDetail,
			this.filterService.distinctEndYear,
			this.filterService.distinctStartYear,
			this.filterService.distinctGovTypeCode ?? of(undefined),
		]).pipe(switchMap(([datasetsDetail, endYear, startYear, govTypeCode]) => {
			return forkJoin(datasetsDetail.map(dataset => {
				const governmentId = govTypeCode && govTypeCode !== ALL_GOVT_TYPES
					? new GovernmentId(govTypeCode) : undefined;
				return this.filingStatusService.getFilingStatusesForYearRange(dataset, startYear, endYear, governmentId)
					.pipe(map(fss => fss.map(fs => {
						const missingFilingReason = missingFilingReasonLookup.find(x => x.value === fs.missingFilingReason);
						return {
							rowHierarchyValues: [fs.mcag],
							columnHierarchyValues: [fs.year],
							annotationType: PivotAnnotationType.noData,
							annotationText: missingFilingReason?.textSentenceCase
						} as DataCellAnnotation;
					})));
			})).pipe(map(x => {
				const annotations = x.flat();
				// Add a "fallback" match...if I search for an MCAG+Year combo in this list and it is not present, means the govt is not active
				annotations.push({
					rowHierarchyValues: [],
					columnHierarchyValues: [],
					annotationType: PivotAnnotationType.noData,
					annotationText: 'Not active'
				} as DataCellAnnotation);
				return annotations;
			}));
		}));

	/**
	 * Totals for field for display year, subcategory detail
	 * Use case - by government type list (pre view model)
	 */
	categoryTotalsBySubcategory =
		combineLatest([this.datasetsDetail, this.fiscalYear, this.dollarsFieldName, this.dollarsId])
		.pipe(switchMap(([datasetsDetail, fiscalYear, dollarsFieldName, dollarsId]) => {
			const queryOverrides = this.generateQueryOverridesForLogicalAccount(dollarsFieldName, dollarsId, true);
			return forkJoin(datasetsDetail.map(dataset => {
				if ((dataset instanceof SnapshotWithDetail || dataset instanceof LiveWithDetail)) {
					return this.financialSummaryService.getSchedule1AggregationsByGovType(dataset, fiscalYear, fiscalYear, queryOverrides);
				} else if (dataset instanceof SchoolsWithDetail) {
					return this.financialSummaryService.getOSPIFinancialReportAggregationsByGovType(dataset, fiscalYear, fiscalYear, queryOverrides);
				} else {
					throw new Error(`Can't resolve dataset ${dataset}`)
				}
			})).pipe(map(x => this.combineAggregationByGovType(x.flat())));

		}));

	/**
	 * Missing filer totals by year for field, no subcategory or gov detail
	 * Use case - trendline graph
	 */
	missingFilersForYearRange = combineLatest([this.datasetsDetail, this.fiscalYear, this.dollarsFieldName, this.dollarsId])
		.pipe(switchMap(([datasetsDetail, fiscalYear, dollarsFieldName, dollarsId]) => {
			const datasetDetail = datasetsDetail.find(dataset => dataset.datasetType === DatasetType.Live || dataset.datasetType === DatasetType.Snapshot);
			if (fiscalYear && (datasetDetail instanceof LiveWithDetail || datasetDetail instanceof SnapshotWithDetail)) {
				const queryOverrides = this.generateQueryOverridesForLogicalAccount(dollarsFieldName, dollarsId, false);
				const filterString = this.financialSummaryService.generateFilterStringForBarsAggregations(datasetDetail, fiscalYear, fiscalYear - DatasetService.DEFAULT_TREND_YEARS + 1, queryOverrides, GovernmentSpecificity.Government);
				return this.financialService.getSchedule1AggregationMissingFilings(datasetDetail, filterString, fiscalYear, fiscalYear - DatasetService.DEFAULT_TREND_YEARS + 1);
			}
			return []; // Only SAO Annual Filer datasets can have "missing" filings
		}));

	/**
	 * Missing filer totals for field for display year, no subcategory or gov detail
	 * (return missing filers last filing that is not display year)
	 * Use case - What’s Missing for FY yyyy? list
	 */
	missingFilersForDisplayYear = combineLatest([this.missingFilersForYearRange, this.fiscalYear])
		.pipe(map(([missingFilers, displayYear]) => {
				// a) group missing filers by mcag
				const groupedMissingFilers = Object.values(missingFilers.reduce((acc: { [x: string]: AggregationByGovt[] }, current: AggregationByGovt) => {
					if (current.mcag) {
						acc[current.mcag] = acc[current.mcag] ?? [];
						acc[current.mcag].push(current);
					}
					return acc;
				}, {}));

				// b) filter out missing filer records for display year and return most recent year w/o zero filing amount
				return groupedMissingFilers.map((gmf: Array<AggregationByGovt>) =>
					gmf.filter((mf: AggregationByGovt) => mf.year !== displayYear && mf.totalAmountExclIntlSrvc !== 0)
						// sort descending and return most recent missing filer record
						.sort(sortByProperty((mf => mf.year), false))[0])
					// and remove missing filer groups that are empty
					.filter(gmf => gmf);
			}
		));

	/**
	 * View model for by dollars page, by government list
	 */
	localGovsForList = this.generateByDollarsForGovernmentId(this.categoryTotalsByGovtForFiscalYear);

	/**
	 * View model for by dollars page, by government type list
	 */
	govTypesForList = this.generateByDollarsForGovernmentId(this.categoryTotalsByGovTypeForFiscalYear);

	/**
	 * View model for by dollars page, by subcategories list
	 */
	subcategoriesForList: Observable<ListRowForDollarsProfile[]> =
		combineLatest([this.generateByDollarsForGovernmentId(this.categoryTotalsBySubcategory), this.logicalAccount])
		.pipe(map(([category, logicalAccount]) => {
		// filter - for subcategory filers that have one subcategory that ids match, return empty array (don't show list)
		// for logging actual subcategory list before filter is set
		// this.logger.log(category));
		return category.length === 1 ? this.findSubcategoryFieldValue(category[0] as ListRowForDollarsProfile) === logicalAccount.id ? [] : category : category;
	}));

	/**
	 * View model for by dollars page, by subcategories list
	 */
	missingFilersForList = this.generateByDollarsForGovernmentId(this.missingFilersForDisplayYear);

	/**
	 * Number of filers for field for display year for by dollars page
	 * Use case - info header x, x governments out of y filers
	 */
	numberOfCategoryFilers = this.categoryTotalsByGovtForFiscalYear.pipe(map(categoryFilers => categoryFilers.length));

	// trend line view models

	/**
	 * View model for category totals for trendline for by dollars page;
	 * sort by year ascending so that the data points are drawn in order
	 */
	categoryTotalsForTrendLine = this.categoryTotalsForYearRange.pipe(map(catTotals =>
		catTotals.sort(sortByProperty(x => x.year)).map(x => new TrendAmount(x.year, x.totalAmountExclIntlSrvc))
	));

	/**
	 * View model for missing filer category totals for trendline (toggle) for by dollars page;
	 * the categoryTotalsForTrendLine is a sorted array of totals per year (ascending by year);
	 * the results returned from this method maintains the order of this input.
	 */
	categoryTotalsMinusMissingFilersTotalsForTrendLine = combineLatest([this.categoryTotalsForTrendLine, this.missingFilersForYearRange])
		.pipe(map(([categoryTotalsForTrendLine, missingFilersForYearRange]) => {
			// a) group missing filers by year
			const groupedMissingFilers = missingFilersForYearRange.reduce((acc: TrendAmount[], curr: AggregationByGovt) => {
				const filer = acc.find((x: TrendAmount) => x.year === curr.year);
				if (filer  && filer.amount !== null) {
					filer.amount += curr.totalAmountExclIntlSrvc;
				} else {
					acc.push(new TrendAmount(curr.year, curr.totalAmountExclIntlSrvc));
				}
				return acc;
			}, []);

			// b) return subtracted missing filer totals from category totals as trend amount view model
			return categoryTotalsForTrendLine.map(ct => {
				const match = groupedMissingFilers.find(gmf => gmf.year === ct.year);
				return new TrendAmount(ct.year, (ct.amount ?? 0) - (match?.amount ?? 0));
			})
		}))

	constructor() {

		// set by dollars profile color
		this.logicalAccount.subscribe(category => {
			if (category && 'fsSectionId' in category && category.fsSectionId) {
				this.color = this.generatePalette(category.fsSectionId);
			} else if (category) {
				this.color = this.generatePalette(category.id);
			} else {
				// something went wrong if it gets to here
				this.color = 'grey';
			}
			this.colorValue = getPaletteValue(this.color);
		});
	}

	// Dollars Selection
	/**
	 * Return view model array (Dollars Categories) for 'By Dollars' Selection Page
	 * This method does the following:
	 * 1) Create array of Dollars Category objects (FS Sections) with nested array of Subcategory account - Basis Account/BARS "2nd Digit" objects (view model)
	 * 2) Call data source aggregation endpoints to populate totals to corresponding FS Section or subcategory
	 * 2) Add total number of filers and number who filed for that fsSection to the corresponding fsSection object
	 * @param datasets
	 * @param displayYear
	 */
	getCategoriesForDollarsSelection(
		datasets: Array<DatasetDetail>,
		displayYear: number
	): Observable<Array<DollarsCategory>>
	{
		const dollarsCategories: Array<DollarsCategory> = [];
		datasets.forEach(dataset => {
			// 1) Create view model structure from this dataset's reference data (merge with any existing)
			dataset?.detail?.financialSummarySections
				// exclude balance sheet
				.filter(fsSection => !DollarsCategoryService.excludedFSSectionIds.includes(fsSection.id))
				.forEach(fsSection => {
					const category = dollarsCategories.find(category => category.id === fsSection.id);
					if (!category) {
						dollarsCategories.push(new DollarsCategory(
							fsSection.name,
							fsSection.id,
							fsSection.sortOrder,
							fsSection.logicalAccount,
							this.generatePalette(fsSection.id),
						));
					}
				});

			// Add all Basic Account level categories to the appropriate FS Sections
			dataset?.detail?.accountDescriptors
				.filter(account => account.acctSgmt === 'BasicAccount' && account.deletedFromHierarchy === false)
				.forEach(account => {
					// match account id with dollars category id
					const category = dollarsCategories.find(category => category.id === account.fsSectionId);

					if (category) {
						const basicAccount = category.basicAccounts.find(basicAccount => basicAccount.id === account.id);
						if (!basicAccount) {
							category.basicAccounts?.push(new BasicAccount(account.name, account.id, account.categoryDisplay, account.logicalAccount))
						}

						// add hint language
						const foundReport = this.financialSummaryService.REPORTS.find(report => report.routeParam === category.logicalAccount);
						if (foundReport?.hint) {
							category.hint = foundReport?.hint;
						}
					}
				});
		});


		// Four calls:
		// a) schedule 1 totals for FsSection for the display year
		// b) schedule 1 totals for BasicAccount for the display year
		// c) schedule 1 number of filers for the display year
		// d) total number of filers for the year (filing status endpoint)
		const totalsForFsSection = this.getTotalsByField(datasets, displayYear, displayYear, 'fsSectionId', null);
		const totalsForBasicAccount = this.getTotalsByField(datasets, displayYear, displayYear, 'basicAccountId', null);
		const filersPerFsSections = this.getFilerCountPerFsSection(datasets, displayYear);
		const expectedFilers = this.getExpectedFilerCount(datasets,displayYear);

		return forkJoin([totalsForFsSection, totalsForBasicAccount, filersPerFsSections, expectedFilers])
			.pipe(map(([totalsForFsSection, totalsForBasicAccount, filersForFsSection, expectedFilers]) => {
				// 2) Add schedule 1 (fsSections and basicAccount) totals to dollar category fields
				totalsForFsSection.forEach(totalForFsSection => {
					// a) Match fsSectionIds and attach total amount to category
					const category = dollarsCategories.find(category =>
						category.id === totalForFsSection.fsSectionId);

					if (category) {
						category.amount = totalForFsSection.totalAmountExclIntlSrvc;
					}
				})

				totalsForBasicAccount.forEach(totalForBasicAccount => {
					// b) Match fsSectionIds AND THEN basicAccountIds and attach total amount to subcategory
					const subCategory = dollarsCategories.find(category =>
						category.id === totalForBasicAccount.fsSectionId)?.basicAccounts.find(subCategory =>
						subCategory.id === totalForBasicAccount.basicAccountId);

					if (subCategory) {
						subCategory.amount = totalForBasicAccount.totalAmountExclIntlSrvc;
					}
				});

				// 3) Add filer data (xxx governments out of xxxx in Washington) to dollar category fields
				dollarsCategories.forEach(dollarCategory => {
					// a) add total amount of expected filers statewide to each dollar category
					dollarCategory.expectedFilersStatewide = expectedFilers;
					// b) match schedule 1 filers for fsSection by fsSectionId and add total to dollar category
					dollarCategory.filersForFsSection = filersForFsSection.find(schedule1Filer => schedule1Filer.fsSectionId === dollarCategory.id)?.numberOfFilers;

					// prepare rankings (set percentage totals and sorts by ranking; filter out any )
					dollarCategory.basicAccounts =
						prepareRankings(dollarCategory.basicAccounts.filter(account => account.amount));
				})

				// return view model
				return dollarsCategories;
			}));
	}

	/**
	 * Return grouped fsSections by total amount of mcags that filed for each section
	 * @param datasets
	 * @param displayYear
	 */
	getFilerCountPerFsSection(
		datasets: Array<DatasetDetail>,
		displayYear: number): Observable<Array<FsSectionFilerCount>>
	{
		const queryOverrides = new Map([
			['fsSectionId', 'ne null'],
			['mcag', 'ne null'],
			['totalAmountExclIntlSrvc', 'ne 0']  // Exclude governments with zero amounts
		]);

		return forkJoin(datasets.map(dataset => {
			const filterString = this.financialSummaryService.generateFilterStringForBarsAggregations(dataset, displayYear, displayYear, queryOverrides, GovernmentSpecificity.Government);
			if ((dataset instanceof SnapshotWithDetail || dataset instanceof LiveWithDetail)) {
				return this.financialService.getSchedule1AggregationTotalFilersPerFsSection(dataset, filterString);
			} else if (dataset instanceof SchoolsWithDetail) {
				return this.financialService.getOSPIAggregationTotalFilersPerFsSection(dataset, filterString);
			} else {
				throw new Error(`Can't resolve dataset ${dataset}`)
			}
		})).pipe(map(x => {
			const combined: Array<FsSectionFilerCount> = [];
			x.flat().forEach(row => {
				const foundRow = combined.find(combinedRow => combinedRow.fsSectionId === row.fsSectionId);
				if (foundRow) {
					foundRow.numberOfFilers += row.numberOfFilers;
				} else {
					combined.push(row);
				}
			});
			return combined;
		}));
	}

	getExpectedFilerCount(
		datasets: Array<DatasetDetail>,
		displayYear: number): Observable<number | undefined>
	{
		return forkJoin(datasets.map(dataset => {
			if ((dataset instanceof SnapshotWithDetail || dataset instanceof LiveWithDetail)) {
				return this.filingStatusService.getSummarizedFilingStatuses(dataset, displayYear).pipe(map(x => x.totalExpectedFilings));
			} else if (dataset instanceof SchoolsWithDetail) {
				const queryOverrides = new Map([
					['fsSectionId', 'ne null'],
					['mcag', 'ne null'],
					['totalAmountExclIntlSrvc', 'ne 0']  // Exclude governments with zero amounts
				]);
				const filterString = this.financialSummaryService.generateFilterStringForBarsAggregations(dataset, displayYear, displayYear, queryOverrides, GovernmentSpecificity.Government);
				return this.financialService.getOSPIAggregationTotalFilers(dataset, filterString);
			} else {
				throw new Error(`Can't resolve dataset ${dataset}`)
			}
		})).pipe(map(x => {
			let totalCount: number | undefined = undefined;
			x.forEach(value => {
				if (value) {
					totalCount = (totalCount ?? 0) + value;
				}
			});
			return totalCount;
		}));
	}

	/**
	 *
	 * @param datasets
	 * @param displayYear
	 *
	 * @deprecated refactor and combine with getExpectedFilerCount() & summarizedFilingStatusesForYear()
	 *
	 * todo - Refactor with with getExpectedFilerCount() & summarizedFilingStatusesForYear()
	 */
	getFilerCount(
		datasets: Array<DatasetDetail>,
		displayYear: number): Observable<number | undefined>
	{
		return forkJoin(datasets.map(dataset => {
			if ((dataset instanceof SnapshotWithDetail || dataset instanceof LiveWithDetail)) {
				return this.filingStatusService.getSummarizedFilingStatuses(dataset, displayYear).pipe(map(x => x.filers));
			} else if (dataset instanceof SchoolsWithDetail) {
				const queryOverrides = new Map([
					['fsSectionId', 'ne null'],
					['mcag', 'ne null'],
					['totalAmountExclIntlSrvc', 'ne 0']  // Exclude governments with zero amounts
				]);
				const filterString = this.financialSummaryService.generateFilterStringForBarsAggregations(dataset, displayYear, displayYear, queryOverrides, GovernmentSpecificity.Government);
				return this.financialService.getOSPIAggregationTotalFilers(dataset, filterString);
			} else {
				throw new Error(`Can't resolve dataset ${dataset}`)
			}
		})).pipe(map(x => {
			let totalCount: number | undefined = undefined;
			x.forEach(value => {
				if (value) {
					totalCount = (totalCount ?? 0) + value;
				}
			});
			return totalCount;
		}));
	}

	/**
	 * Generate color palette for each fsSection
	 * @param fsSectionId
	 */
	generatePalette(fsSectionId: FinancialSummarySectionId): Palette {
		if (fsSectionId === 20 || fsSectionId === 25) {
			return 'green';
		} else if (fsSectionId === 30 || fsSectionId === 35) {
			return 'purple';
		} else if (fsSectionId === 10 || fsSectionId === 40) {
			return 'blue';
		} else {
			// default case (something went wrong if return is grey)
			return 'grey';
		}
	}

	// end section

	// dollars info header
	getTotalsByField(
		datasets: Array<DatasetDetail>,
		endYear: number,
		startYear: number,
		dollarsFieldName: string,
		dollarsFieldId: number | null): Observable<Array<AggregationByGovType>>
	{
		const queryOverrides = this.generateQueryOverridesForLogicalAccount(dollarsFieldName, dollarsFieldId, false);
		return forkJoin(datasets.map(dataset => {
			if ((dataset instanceof SnapshotWithDetail || dataset instanceof LiveWithDetail)) {
				return this.financialSummaryService.getSchedule1AggregationsByGovType(dataset, endYear, startYear, queryOverrides);
			} else if (dataset instanceof SchoolsWithDetail) {
				return this.financialSummaryService.getOSPIFinancialReportAggregationsByGovType(dataset, endYear, startYear, queryOverrides);
			} else {
				throw new Error(`Can't resolve dataset ${dataset}`)
			}
		})).pipe(map(x => this.combineAggregationByGovType(x.flat())));

	}

	combineAggregationByGovType(data: Array<AggregationByGovType>) {
		const combined: Array<AggregationByGovType> = [];
		data.forEach(dataRow => {
			const foundRow = combined.find(combinedRow => combinedRow.year === dataRow.year && combinedRow.govTypeCode === dataRow.govTypeCode
				&& combinedRow.countyCode === dataRow.countyCode && combinedRow.fsSectionId === dataRow.fsSectionId
				&& combinedRow.fundCategoryId === dataRow.fundCategoryId && combinedRow.fundTypeId === dataRow.fundTypeId
				&& combinedRow.basicAccountId === dataRow.basicAccountId && combinedRow.subAccountId === dataRow.subAccountId
				&& combinedRow.elementId === dataRow.elementId && combinedRow.subElementId === dataRow.subElementId);
			if (foundRow) {
				foundRow.totalAmount += dataRow.totalAmount;
				foundRow.totalAmountExclIntlSrvc += dataRow.totalAmountExclIntlSrvc;
			} else {
				combined.push(Object.assign({},dataRow));
			}
		});
		return combined;
	}

	/**
	 * Generate query overrides for by dollar profile schedule 1 aggregations
	 * @param fieldName
	 * @param fieldId - if null, pass string 'ne null' (get all totals for this field)
	 * @param includeSubcategoryDetails
	 * @param fundNode - if undefined or allFunds, apply no filter
	 * @param counties - if undefined or all counties, apply no filter
	 * @param accountingBases - if undefined or all accounting bases, apply no filter
	 * @param govTypeCode - if undefined, apply no filter
	 */
	generateQueryOverridesForLogicalAccount(
		fieldName: string,
		fieldId: number | null,
		includeSubcategoryDetails: boolean,
		fundNode?: SelectedFundNode,
		counties?: SelectedCounties,
		accountingBases?: SelectedAccountingBases,
		govTypeCode?: ALL_GOVT_TYPES | GovTypeCode
	): Map<string, string> {
		const queryOverrides = new Map();

		// Any category above given field is set to not equal null for the query
		// a) index number in order hierarchy
		const index = DollarsCategoryService.defaultBARSHierarchy.findIndex(name => name === fieldName);
		// b) slice array at that point (starting at 0 index)
		const slicedArray = DollarsCategoryService.defaultBARSHierarchy.slice(0, index);
		// c) iterate over sliced array and set query overrides to not equal null for any fields include in it
		slicedArray.forEach(fieldName => queryOverrides.set(fieldName, 'ne null'));

		// Set actual explicit field that we're retrieving data for
		queryOverrides.set(fieldName, fieldId === null ? 'ne null' : `eq ${fieldId}`);

		if (includeSubcategoryDetails) {
			// if include subcategory detail is set to true, return level of detail below fieldName in Bars Hierarchy
			const childFieldName = DollarsCategoryService.defaultBARSHierarchy[index + 1];
			queryOverrides.set(childFieldName, 'ne null');
		}

		// Set fund category or fund type overrides if needed
		if (fundNode && fundNode.value !== null && fundNode.field !== FundField.allFunds) {
			if (fundNode.field !== FundField.fundCategoryId) {
				queryOverrides.set(FundField.fundCategoryId, 'ne null');
				if (fundNode.field !== FundField.fundTypeId) {
					queryOverrides.set(FundField.fundTypeId, 'ne null');
				}
			}

			if (typeof fundNode.value === 'number') {
				queryOverrides.set(fundNode.field, `eq ${fundNode.value}`);
			} else {
				queryOverrides.set(fundNode.field, `eq '${fundNode.value}'`);
			}
		}

		// Set counties filter/overrides if needed
		if (counties && counties !== ALL_COUNTIES) {
			queryOverrides.set(`countyCode`, `in (${counties.join(',')})`);
		}

		// Set accounting bases filter/overrides if needed
		if (accountingBases && accountingBases !== ALL_ACCOUNTING_BASES) {
			queryOverrides.set(`acctBasisTypeId`, `in (${accountingBases.join(',')})`);
		}

		// Set Gov Type Code filter if needed
		if (govTypeCode && govTypeCode !== ALL_GOVT_TYPES) {
			queryOverrides.set(`govTypeCode`, `eq '${govTypeCode}'`);
		}

		return queryOverrides;
	}

	/**
	 * Return fiscal year for display based on dataset
	 * @param dataset
	 */
	getFiscalYearForDisplay(dataset: DatasetDetail | null): Observable<number | null> {
		return this.financialSummaryService.getFiscalYearForDisplay(dataset);
	}

	// Type guard to assert an object is an AccountDescriptor when categoryDisplay is not strictly undefined
	isAccountDescriptor = (logicalAccount: FinancialSummarySection | AccountDescriptor)
		: logicalAccount is AccountDescriptor => {
		return (logicalAccount as AccountDescriptor).categoryDisplay !== undefined;
	}

	// Type guard to assert that an object is a BARS Account when categoryDisplay is a string of length greater than
	// zero.
	isBARSAccount = (logicalAccount: FinancialSummarySection | AccountDescriptor): logicalAccount is AccountDescriptor => {
		return (logicalAccount as AccountDescriptor).categoryDisplay?.length > 0;
	}

	// end section

	// By dollars Profile

	/**
	 * 	View model for all dollars profile page lists
	 * 	Identifies type by looking up properties on schedule1AggregationByGov or schedule1AggregationByGovType (or any type that extends it) in this order:
	 * 	government
	 * 	government type
	 * 	dollar category (in bars hierarchy) (comes from schedule1AggregationByGovType endpoint)
	 * 	assigns name, navRoute, amount, and ranking
	 * 	Note: In the case of the govType list, pass schedule1AggregationByGovt already extended as ListRowDollarsProfile with number of govs for each gov type
	 * 	@param aggregations
	 */
	generateByDollarsForGovernmentId(aggregations: Observable<AggregationByGovt[] | AggregationByGovType[]>): Observable<Array<ListRowForDollarsProfile>> {
		return combineLatest([
			aggregations,
			this.fitApiService.governmentsForList(),
			this.fitApiService.governmentTypesForList,
			this.fitApiService.countiesForList,
			this.datasetsDetail
		]).pipe(map(([aggregations, localGovs, govTypes, counties, datasetsDetail]) => {
			const rows = aggregations.map(aggregation => {
				const localGov: LocalGovernment | undefined = localGovs.find(gov => gov.mcag === (aggregation as AggregationByGovt).mcag);
				const govType: GovernmentType | undefined = govTypes.find(govType => govType.code === aggregation.govTypeCode);
				const county: County | undefined = counties.find(county => county.countyCode === aggregation.countyCode);

				let subcategory: any | undefined = undefined;
				datasetsDetail.forEach(datasetDetail => {
					const reference = datasetDetail?.detail.getReference('accountDescriptors', this.findSubcategoryFieldValue(aggregation));
					if (reference) {
						subcategory = reference;
					}
				});

				return {
					...aggregation,
					name: localGov?.sortableFullNameForSearching ?? govType?.descriptionPlural ?? subcategory?.name,
					navRoute: localGov ? ['explore', 'government', (aggregation as AggregationByGovt).mcag] : govType ? ['explore', 'government-type', aggregation.govTypeCode] : ['explore', 'dollars', 'bars', subcategory?.logicalAccount],
					amount: aggregation.totalAmountExclIntlSrvc,
					govTypeName: localGov ? govType?.description : null,
					navRouteForGovType: localGov ? ['explore', 'government-type', aggregation.govTypeCode] : null,
					county: localGov ? county?.countyName : null
				} as ListRowForDollarsProfile
			})

			return prepareRankings(rows);
		}));
	}

	/**
	 * Find the subcategory field value that is the lowest level of detail for schedule1AggregationByGovt
	 * 	@param aggregationByGovType
	 */
	findSubcategoryFieldValue(aggregationByGovType: AggregationByGovType): number {
		const reversedHierarchy = [...DollarsCategoryService.defaultBARSHierarchy].reverse();
		const fieldName = reversedHierarchy.find(account => aggregationByGovType[account as keyof AggregationByGovType] !== null);
		return aggregationByGovType[fieldName as keyof AggregationByGovType] as number;
	}

	/**
	 * Navigation function for by dollars profile
	 * Takes user to top of page when on the same route
	 * @param category
	 */
	navigateToProfile(category: ListRowForDollarsProfile | null) {
		if (category) {
			// { fragment: 'scroll-to-top' }
			this.router.navigate(category.navRoute).then(() => {
					// scroll to top if navigating to same page
					document.getElementById('scroll-to-top')?.scrollIntoView(true);
				}
			)
		}
	}

	/**
	 * Get logical account based on a list of hierarchy values and field names (e.g. used by data cell navigation)
	 *
	 * @param hierarchyValues
	 * @param hierarchyFieldNames
	 * @param financialSummarySections
	 * @param accountDescriptors
	 *
	 * @deprecated
	 */
	static getDrilldownLogicalAccountDeprecated =
		(
			hierarchyValues: (string | number)[],
			hierarchyFieldNames: string[],
			financialSummarySections: FinancialSummarySection[],
			accountDescriptors: AccountDescriptor[]
		) => {
			const value = hierarchyValues[hierarchyValues.length - 1];
			// if field name is a financial summary logical account use financial summary lookup else use account descriptors
			const logicalAccounts = hierarchyFieldNames[hierarchyValues.length - 1] === 'fsSectionId' ? financialSummarySections : accountDescriptors;

			return logicalAccounts.find(f => f.id === value);
		}

	// Used to store the pivotTable results for the By Government & By Government Type pages
	pivotTable?: PivotTable<any>;

	// Download pivotTable contents
	downloadUnsubscribe = new Subject<void>();
	downloadClick = () => {
		const workbook = new Excel.Workbook();
		if (this.pivotTable) {
			this.downloadService.getPlaceholderExtractDownload(
				this.pivotTable,
				workbook,
				`$ ${this.selectedTab?.text}`,
				`Dollars ${this.selectedTab?.text}`,
				null,
				`${this.categoryTitle} ${this.selectedTab?.text}`,
				this.annotation,
				this.filterService.startYear,
				this.filterService.endYear,
				this.downloadUnsubscribe
			);
		}
	};
}

// Type for view model
export interface ListRowForDollarsProfile extends Ranking, AggregationByGovt, AggregationByGovType {
	name: string;
	navRoute: Array<string>;

	// for gov list
	navRouteForGovType?: Array<string>;
	govTypeName?: string
	county?: string;

	// for gov type list
	numberOfGovs?: number;
}
